import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { useLanguage, LangToggle } from '../context/LanguageContext'
import { useSubscription } from '../context/SubscriptionContext'
import { usePushNotifications } from '../hooks/usePushNotifications'

export default function Profile({ darkMode, setDarkMode }) {
  const { user, signOut } = useAuth()
  const { t, lang, toggleLang } = useLanguage()
  const { plan: userPlan, subscription } = useSubscription()
  const { isSupported: pushSupported, isSubscribed: pushEnabled, subscribe: enablePush, unsubscribe: disablePush } = usePushNotifications()
  const [profile, setProfile] = useState(null)
  const [warranties, setWarranties] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeSection, setActiveSection] = useState(null)

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwLoading, setPwLoading] = useState(false)
  const [pwError, setPwError] = useState('')
  const [pwSuccess, setPwSuccess] = useState('')

  const [editName, setEditName] = useState('')
  const [nameLoading, setNameLoading] = useState(false)
  const [nameSuccess, setNameSuccess] = useState('')

  const navigate = useNavigate()

  useEffect(() => { fetchData() }, [])

  const fetchData = async () => {
    const [{ data: prof }, { data: wars }] = await Promise.all([
      supabase.from('profiles').select('*').eq('id', user.id).single(),
      supabase.from('warranties').select('id, expiry_date').eq('user_id', user.id)
    ])
    setProfile(prof)
    setEditName(prof?.full_name || '')
    setWarranties(wars || [])
    setLoading(false)
  }

  const handleChangePassword = async () => {
    setPwError(''); setPwSuccess('')
    if (!newPassword || !confirmPassword) return setPwError(t.fillAllFields)
    if (newPassword !== confirmPassword) return setPwError(t.passwordMismatch)
    if (newPassword.length < 6) return setPwError(t.passwordTooShort)
    setPwLoading(true)
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    setPwLoading(false)
    if (error) return setPwError(error.message)
    setPwSuccess(t.passwordChanged)
    setNewPassword(''); setConfirmPassword('')
    setTimeout(() => { setPwSuccess(''); setActiveSection(null) }, 3000)
  }

  const handleUpdateName = async () => {
    if (!editName.trim()) return
    setNameLoading(true)
    await supabase.from('profiles').update({ full_name: editName }).eq('id', user.id)
    setProfile(p => ({ ...p, full_name: editName }))
    setNameLoading(false)
    setNameSuccess(t.nameSaved)
    setTimeout(() => { setNameSuccess(''); setActiveSection(null) }, 2000)
  }

  const totalWarranties = warranties.length
  const activeCount     = warranties.filter(w => Math.ceil((new Date(w.expiry_date) - new Date()) / 86400000) >= 0).length
  const expiredCount    = totalWarranties - activeCount

  const initials = profile?.full_name
    ? profile.full_name.split(' ').map(n => n[0]).join('').slice(0, 2)
    : user.email[0].toUpperCase()

  return (
    <div className="app-layout">
      <div className="topbar topbar-gradient">
        <button className="topbar-btn" style={{ color: '#fff' }} onClick={() => navigate('/dashboard')} aria-label="Back">{t.dir === 'rtl' ? '→' : '←'}</button>
        <div className="topbar-logo">
          <span style={{ color: '#fff', fontWeight: 900, fontSize: '1.1rem' }}>{t.appName} 🛡️</span>
        </div>
        <LangToggle />
      </div>

      <div className="main-content" style={{ paddingBottom: 40 }}>

        <div className="profile-hero">
          <div className="profile-avatar">{initials}</div>
          <div className="profile-name">{profile?.full_name || t.userFallback}</div>
          <div className="profile-email">{user.email}</div>
        </div>

        <div className="stats-row" style={{ marginBottom: 24 }}>
          <div className="stat-card">
            <div className="stat-card-num">{totalWarranties}</div>
            <div className="stat-card-lbl">{t.totalWarranties}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-num" style={{ color: '#4ADE80' }}>{activeCount}</div>
            <div className="stat-card-lbl">{t.active}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-num" style={{ color: '#ff8080' }}>{expiredCount}</div>
            <div className="stat-card-lbl">{t.expired}</div>
          </div>
        </div>

        <div className="profile-menu">

          {/* Edit Name */}
          <div className="profile-menu-item" onClick={() => setActiveSection(activeSection === 'name' ? null : 'name')}>
            <div className="pmi-right"><span className="pmi-icon">👤</span><span className="pmi-label">{t.editName}</span></div>
            <span className="pmi-arrow">{activeSection === 'name' ? '↑' : (t.dir === 'rtl' ? '←' : '→')}</span>
          </div>
          {activeSection === 'name' && (
            <div className="profile-section-body">
              <input className="form-input" id="profile-name" value={editName} onChange={e => setEditName(e.target.value)} placeholder={t.fullNameLabel} aria-label={t.fullNameLabel} style={{ marginBottom: 12 }} />
              {nameSuccess && <div className="success-msg">{nameSuccess}</div>}
              <button className="btn-full" onClick={handleUpdateName} disabled={nameLoading}>
                {nameLoading ? t.savingName : t.saveName}
              </button>
            </div>
          )}

          {/* Change Password */}
          <div className="profile-menu-item" onClick={() => setActiveSection(activeSection === 'password' ? null : 'password')}>
            <div className="pmi-right"><span className="pmi-icon">🔒</span><span className="pmi-label">{t.changePassword}</span></div>
            <span className="pmi-arrow">{activeSection === 'password' ? '↑' : (t.dir === 'rtl' ? '←' : '→')}</span>
          </div>
          {activeSection === 'password' && (
            <div className="profile-section-body">
              <div className="form-group">
                <label className="form-label" htmlFor="profile-new-pw">{t.newPasswordLabel}</label>
                <input className="form-input" id="profile-new-pw" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="profile-confirm-pw">{t.confirmPasswordLabel}</label>
                <input className="form-input" id="profile-confirm-pw" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" />
              </div>
              {pwError   && <div className="error-msg">{pwError}</div>}
              {pwSuccess && <div className="success-msg">{pwSuccess}</div>}
              <button className="btn-full" onClick={handleChangePassword} disabled={pwLoading}>
                {pwLoading ? t.changingPassword : t.changePasswordBtn}
              </button>
            </div>
          )}

          {/* Dark Mode */}
          <div className="profile-menu-item" onClick={() => setDarkMode(!darkMode)}>
            <div className="pmi-right">
              <span className="pmi-icon">{darkMode ? '🌙' : '☀️'}</span>
              <span className="pmi-label">{darkMode ? t.darkModeLabel : t.lightModeLabel}</span>
            </div>
            <div className={`toggle-switch ${darkMode ? 'on' : ''}`}><div className="toggle-knob" /></div>
          </div>

          {/* Notifications */}
          {pushSupported && (
            <div className="profile-menu-item" onClick={() => pushEnabled ? disablePush() : enablePush()}>
              <div className="pmi-right">
                <span className="pmi-icon">🔔</span>
                <span className="pmi-label">{lang === 'ar' ? 'التنبيهات' : 'Notifications'}</span>
              </div>
              <div className={`toggle-switch ${pushEnabled ? 'on' : ''}`}><div className="toggle-knob" /></div>
            </div>
          )}

          {/* Subscription */}
          <div className="profile-menu-item" onClick={() => navigate('/pricing')}>
            <div className="pmi-right">
              <span className="pmi-icon">⭐</span>
              <span className="pmi-label">{t.subscriptionLabel}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '0.82rem', color: userPlan === 'premium' ? '#4ADE80' : 'var(--ash)' }}>
                {userPlan === 'premium' ? t.premiumPlan : t.freePlan}
              </span>
              <span className="pmi-arrow">{t.dir === 'rtl' ? '←' : '→'}</span>
            </div>
          </div>

          {/* Language */}
          <div className="profile-menu-item" onClick={toggleLang}>
            <div className="pmi-right">
              <span className="pmi-icon">🌐</span>
              <span className="pmi-label">{t.languageLabel}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '0.82rem', color: 'var(--ash)' }}>{lang === 'ar' ? 'العربية' : 'English'}</span>
              <div className="toggle-switch on" style={{ background: 'var(--orange)' }}><div className="toggle-knob" /></div>
            </div>
          </div>

          {/* Logout */}
          <div className="profile-menu-item logout-item" onClick={signOut}>
            <div className="pmi-right"><span className="pmi-icon">🚪</span><span className="pmi-label" style={{ color: '#ff8080' }}>{t.logout}</span></div>
            <span className="pmi-arrow">{t.dir === 'rtl' ? '←' : '→'}</span>
          </div>

        </div>
      </div>
    </div>
  )
}
